import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PC_FAQ_CATEGORIES } from "@/content/police-clearance-corpus/faq-defs";

const RELATED_SERVICES_TH = [
  { slug: "notary-apostille-poa", label: "Apostille & POA" },
  { slug: "mfa-consular", label: "รับรองกงสุล MFA" },
  { slug: "notary-public", label: "Notary Public" },
  { slug: "translation-certified-all", label: "แปลรับรองเอกสาร" },
  { slug: "police-clearance-intl", label: "ตรวจประวัติต่างประเทศ" },
];

const RELATED_SERVICES_EN = [
  { slug: "hague-apostille-2026", label: "Apostille 2026" },
  { slug: "mfa-legalization", label: "MFA Legalization" },
  { slug: "notary-public", label: "Notary Public" },
  { slug: "certified-translation", label: "Certified Translation" },
  { slug: "fbi-background-check-thailand", label: "FBI Background Check" },
];

/**
 * Cross-link block for Police Clearance province pages — surfaces the 7 FAQ
 * sub-topics (apostille, urgent, documents, …) plus related service pillars so
 * crawlers and AI agents can discover the full cluster from any province page.
 */
export function PcProvinceCrossLinks({
  provinceLabel,
  lang = "th",
}: {
  provinceLabel: string;
  lang?: "th" | "en";
}) {
  const isEn = lang === "en";
  return (
    <section className="border-b border-border bg-muted/20">
      <div className="mx-auto max-w-5xl px-4 py-12">
        <h2 className="text-2xl font-bold">
          {isEn
            ? `Related answers & services for ${provinceLabel}`
            : `หัวข้อและบริการที่เกี่ยวข้อง — ${provinceLabel}`}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {isEn
            ? "Deep-dive answers by topic, curated by our Notarial Services Attorneys."
            : "เจาะลึกทีละหัวข้อ พร้อมบริการต่อเนื่องหลังได้ใบตรวจประวัติ"}
        </p>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {PC_FAQ_CATEGORIES.map((c) =>
            isEn ? (
              <Link
                key={c.slug}
                to="/en/police-clearance/faq/$category"
                params={{ category: c.slug }}
                className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-gold"
              >
                <span>
                  <span className="block text-sm font-bold text-primary">{c.titleEn}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{c.descEn}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-gold" />
              </Link>
            ) : (
              <Link
                key={c.slug}
                to="/police-clearance/faq/$category"
                params={{ category: c.slug }}
                className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-gold"
              >
                <span>
                  <span className="block text-sm font-bold text-primary">{c.titleTh}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">{c.descTh}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-gold" />
              </Link>
            ),
          )}
        </div>

        <h3 className="mt-8 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {isEn ? "Continue with" : "บริการต่อเนื่อง"}
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {isEn
            ? RELATED_SERVICES_EN.map((s) => (
                <Link
                  key={s.slug}
                  to="/en/services/$service"
                  params={{ service: s.slug }}
                  className="rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold hover:border-gold"
                >
                  {s.label}
                </Link>
              ))
            : RELATED_SERVICES_TH.map((s) => (
                <Link
                  key={s.slug}
                  to="/services/$service"
                  params={{ service: s.slug }}
                  className="rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold hover:border-gold"
                >
                  {s.label}
                </Link>
              ))}
          {isEn ? (
            <Link
              to="/en/police-clearance/faq"
              className="rounded-full border border-gold bg-gold/10 px-4 py-2 text-xs font-semibold"
            >
              All Police Clearance FAQ →
            </Link>
          ) : (
            <Link
              to="/police-clearance/faq"
              className="rounded-full border border-gold bg-gold/10 px-4 py-2 text-xs font-semibold"
            >
              คำถามทั้งหมด →
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}